import React, { useMemo } from 'react';
import { Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { APOffsetRow, GrandTotal, QueryResponse } from '../../../services/h303Api';

interface ResultTableProps {
  result: QueryResponse;
  reportType: string;
}

const fmtAmt = (v: number): string => {
  if (v == null || v === 0) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const detailColumns: ColumnsType<APOffsetRow & { _key: string }> = [
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 80,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`.trim(),
  },
  {
    title: '廠商代號',
    dataIndex: 'supplierCode',
    key: 'supplierCode',
    width: 120,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.supplierName || '').trim()}`.trim(),
  },
  {
    title: '採購人員',
    dataIndex: 'buyerCode',
    key: 'buyerCode',
    width: 100,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.buyerName || '').trim()}`.trim(),
  },
  {
    title: '年度月份',
    dataIndex: 'yearMonth',
    key: 'yearMonth',
    width: 80,
  },
  {
    title: '幣別',
    dataIndex: 'currency',
    key: 'currency',
    width: 60,
  },
  {
    title: '應付金額',
    dataIndex: 'amountDue',
    key: 'amountDue',
    width: 120,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '已付金額',
    dataIndex: 'amountPaid',
    key: 'amountPaid',
    width: 120,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
];

const totalColumns: ColumnsType<GrandTotal & { _key: string }> = [
  {
    title: '幣別',
    dataIndex: 'currency',
    key: 'currency',
    width: 80,
  },
  {
    title: '應付總額',
    dataIndex: 'amountDue',
    key: 'amountDue',
    width: 150,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '已付總額',
    dataIndex: 'amountPaid',
    key: 'amountPaid',
    width: 150,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result, reportType }) => {
  const detailData = useMemo(() => {
    if (!result.details) return [];
    return result.details.map((row, i) => ({
      ...row,
      _key: `${row.deptCode}-${row.supplierCode}-${row.buyerCode}-${row.yearMonth}-${row.currency}-${i}`,
    }));
  }, [result.details]);

  const totalData = useMemo(() => {
    if (!result.totals) return [];
    return result.totals.map((row, i) => ({
      ...row,
      _key: `total-${row.currency}-${i}`,
    }));
  }, [result.totals]);

  // Reorder columns based on report type
  const columns = useMemo(() => {
    if (reportType === 'supplierDept') {
      // Supplier first, then dept
      const cols = [...detailColumns];
      const supplierCol = cols.splice(1, 1)[0]; // remove supplier
      cols.unshift(supplierCol); // put at front
      return cols;
    }
    return detailColumns;
  }, [reportType]);

  const items = [
    {
      key: 'details',
      label: `明細 (${result.totalCount} 筆)`,
      children: (
        <Table
          columns={columns}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 800 }}
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `共 ${total} 筆` }}
        />
      ),
    },
    {
      key: 'totals',
      label: '幣別總計',
      children: (
        <Table
          columns={totalColumns}
          dataSource={totalData}
          rowKey="_key"
          size="small"
          pagination={false}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default React.memo(ResultTable);
